import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Download, AlertCircle, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface Invoice {
  id: string;
  created_at: string;
  amount: number;
  status: string;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
  subscription_id: string;
}

interface InvoiceListProps {
  userId: string;
}

const InvoiceList: React.FC<InvoiceListProps> = ({ userId }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [diagnosticInfo, setDiagnosticInfo] = useState<any>(null);

  useEffect(() => {
    fetchInvoices();
  }, [userId]);

  const fetchInvoices = async () => {
    try {
      setError(null);
      setDiagnosticInfo(null);
      //console.log('Fetching invoices for user:', userId);
      
      // Get user's profile to check for organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', userId)
        .single();
        
      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Profile fetch error:', profileError);
        setDiagnosticInfo({ profileError });
        throw profileError;
      }
      
      if (!profileData || !profileData.organization_id) {
        console.log('No organization found for user');
        setDiagnosticInfo({ message: 'No organization found for user' });
        setInvoices([]);
        setLoading(false);
        return;
      }
      
      const organizationId = profileData.organization_id;
      //console.log('Found organization ID:', organizationId);
      
      // Get all organization's subscriptions
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('id')
        .eq('organization_id', organizationId);

      if (subscriptionError) {
        console.error('Subscription fetch error:', subscriptionError);
        throw subscriptionError;
      }

      if (!subscriptionData || subscriptionData.length === 0) {
        console.log('No subscriptions found for organization');
        setInvoices([]);
        return;
      }

      const subscriptionIds = subscriptionData.map(sub => sub.id);
      //console.log('Found subscription IDs:', subscriptionIds);
      
      // Fetch invoices for all organization subscriptions
      const { data: invoiceData, error: invoiceError } = await supabase
        .from('invoice_history')
        .select('*')
        .in('subscription_id', subscriptionIds)
        .order('created_at', { ascending: false });
      
      if (invoiceError) {
        console.error('Error fetching invoices:', invoiceError);
        throw invoiceError;
      }

      //console.log('Fetched invoices:', invoiceData);
      setInvoices(invoiceData || []);
      
    } catch (error: any) {
      console.error('Error in fetchInvoices:', error);
      setError(error.message);
      toast.error(`Failed to load invoices: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoice: Invoice) => {
    try {
      // Prefer hosted invoice URL if available, fallback to PDF
      const downloadUrl = invoice.hosted_invoice_url || invoice.invoice_pdf;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
      } else {
        toast.error('No invoice download link available');
      }
    } catch (error: any) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading invoices...</div>;
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-red-800 mb-2">Error loading invoices</h4>
              <p className="text-sm text-red-700 mb-4">{error}</p>
              {diagnosticInfo && (
                <div className="text-xs bg-white/50 p-3 rounded border border-red-200 mb-4 overflow-auto">
                  <pre>{JSON.stringify(diagnosticInfo, null, 2)}</pre>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchInvoices()}
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (invoices.length === 0) {
    return (
      <Card className="border-gray-200 bg-gray-50">
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground mb-2">No invoices found</p>
          <p className="text-xs text-muted-foreground">
            Invoices will appear here after your next billing cycle.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((invoice) => (
            <TableRow key={invoice.id}>
              <TableCell>{format(new Date(invoice.created_at), 'MMM d, yyyy')}</TableCell>
              <TableCell>${(invoice.amount / 100).toFixed(2)}</TableCell>
              <TableCell>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  invoice.status === 'paid' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {invoice.status}
                </span>
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDownloadInvoice(invoice)}
                  disabled={!invoice.hosted_invoice_url && !invoice.invoice_pdf}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default InvoiceList; 